import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {User, UserImpl} from '../api/user-interfaces';
import {BaseUserService} from '@shared/services/api/base-user.service';
import {ClassTransformer} from 'class-transformer';
import {HttpUtilityService} from '@app/shared/services/api/http-utility-service';
import {UserEntity} from '@shared/services/api/user-entity';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';

enum USER_OPERATION {
  USERS = 'users',
  USER_ID_KEY = 'user_id'
}

interface CreateUserAPIResponse {
  used_id:string
}

export const userResolver: ResolveFn<User> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<User> => {
  return inject(UserService).getUser(route.paramMap.get('id'));
};

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseUserService {

  public static readonly modelMapping = {
    User: UserImpl
  };

  private headers: HttpHeaders = this.getHeaders();
  private readonly logger: AceLogger;
  private readonly USERS_PAGE_LIMIT = 50;


  constructor(private http: HttpClient,
              private classTransformer: ClassTransformer,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(UserService.name);
  }

  public static userCompare(user1: User, user2: User): number {
    const fullName1 = user1?.fullName;
    const fullName2 = user2?.fullName;
    if (fullName1 == null && fullName2 == null) {
      return 0;
    }
    if (fullName1 == null) {
      return 1;
    }
    if (fullName2 == null) {
      return -1;
    }

    return fullName1.localeCompare(fullName2);
  }

  public static userEntityCompare(entity1: UserEntity, entity2: UserEntity): number {
    const entityName1 = entity1?.name;
    const entityName2 = entity2?.name;
    if (entityName1 == null && entityName2 == null) {
      return 0;
    }
    if (entityName1 == null) {
      return 1;
    }
    if (entityName2 == null) {
      return -1;
    }

    return entityName1.localeCompare(entityName2);
  }

  public static userEntityAndFullNameCompare(user1: User, user2: User): number {
    const entityCompare = UserService.userEntityCompare(user1?.entity, user2?.entity);
    return entityCompare !== 0 ? entityCompare : UserService.userCompare(user1, user2);
  }

  private fetchUsers(paramName: string, paramValue: number, first: number, max: number, searchParam?: string): Observable<User[]> {
    let params = new HttpParams().set(paramName, paramValue.toString()).set('first', first.toString()).set('max', max.toString());
    if (searchParam && searchParam.trim()) {
      params = params.set('search', searchParam.trim());
    }
    const options = {headers: this.headers, params: params};

    return this.http.get(this.getGateway() + '/users/users', options)
      .pipe(map((response: APIResponse<User[]>) => {
        const userResponse: User[] = this.httpUtilityService.extractResponseData(response, USER_OPERATION.USERS);
        return this.classTransformer.plainToInstance(UserImpl, userResponse);
      }));
  }

  getCustomerUsers(customerId: number, first: number, max: number, searchParam?: string): Observable<User[]> {
    return this.fetchUsers('customer_id', customerId, first, max, searchParam);
  }

  getAllCustomerUsers(customerId): Observable<User[]> {
    return this.httpUtilityService.pageUntilComplete((pageInfo) => this.getCustomerUsers(customerId, pageInfo.first, pageInfo.rows)
        .pipe(map((users: User[]) => ({ count: null, data: users }))),
      {first: 0, rows: this.USERS_PAGE_LIMIT});

  }

  getClientUsers(clientId: number, first: number, max: number, searchParam?: string): Observable<User[]> {
    return this.fetchUsers('client_id', clientId, first, max, searchParam);
  }

  getAllClientUsers(clientId): Observable<User[]> {
    return this.httpUtilityService.pageUntilComplete((pageInfo) => this.getClientUsers(clientId, pageInfo.first, pageInfo.rows)
        .pipe(map((users: User[]) => ({ count: null, data: users }))),
      {first: 0, rows: this.USERS_PAGE_LIMIT});
  }

  getContractorUsers(contractorId: number, first: number, max: number, searchParam?: string): Observable<User[]> {
    return this.fetchUsers('contractor_id', contractorId, first, max, searchParam);
  }

  getUser(id: string, fetchEntity = false): Observable<User> {
    let params = new HttpParams();
    if (fetchEntity) {
      // HttpParams object is immutable! So calling set() returns a new object
      params = params.set('include_entity', fetchEntity.toString());
    }
    const options = {headers: this.headers, ...(params.keys().length && {params: params})};
    return this.http.get(this.getGateway() + '/users/users/' + id, options).pipe(map((response: {data: {user: User}}) => {
      return this.classTransformer.plainToInstance(UserImpl, response.data.user);
    }));
  }

  getUsers(userIds: string[], fetchEntity = false): Observable<User[]> {
    const wrapper = (userIds: string[]): Observable<User[]> => {
      let params = new HttpParams();
      if (fetchEntity) {
        params = params.set('include_entity', fetchEntity.toString());
      }
      for (let id of userIds) {
        params = params.append('user_id', id);
      }
      const options = {headers: this.headers, ...(params.keys().length && {params: params})};
      return this.http.get(this.getGateway() + '/users/users', options).pipe(
        map((response: {data: {users: User[]}}) =>
          response.data.users.map(user => this.classTransformer.plainToInstance(UserImpl, user))));
    };
    return this.httpUtilityService.requestPartitioned(wrapper, userIds, this.USERS_PAGE_LIMIT);
  }

  createCustomerUser(customerId: number, user: User): Observable<string> {
    user.customer_id = customerId;
    return this.createUser(user);
  }

  createClientUser(clientId: number, user: User): Observable<string> {
    user.client_id = clientId;
    return this.createUser(user);
  }

  createContractorUser(contractorId: number, user: User): Observable<string> {
    user.contractor_id = contractorId;
    return this.createUser(user);
  }

  private createUser(user: User): Observable<string>  {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/users/users', user, options)
      .pipe(map((response: APIResponse<CreateUserAPIResponse>) => this.httpUtilityService.extractResponseData(response, USER_OPERATION.USER_ID_KEY)));
  }

  updateUser(user: User): Observable<Object> {
    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/users/users/' + user.id, user, options);
  }

  resetPassword(userId: string, password: string, temporary: boolean): Observable<any> {
    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/users/users/' + userId + '/password', {password: password, temporary: temporary}, options);
  }

  resetAccount(userId: string, includeTac?: boolean, lifespan?: number): Observable<Object> {
    const body: any = {};

    // Force the user to accept Terms and Conditions on login
    if (includeTac != null) {
      body.include_tac = includeTac;
    }

    if (lifespan != null) {
      body.lifespan = lifespan;
    }

    const options = {headers: this.headers};
    return this.http.put(this.getGateway() + '/users/users/' + userId + '/resetaccount', body, options);
  }

  deleteUser(id: string): Observable<Object> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/users/users/' + id, options);
  }

}
